import React from 'react';

interface ManualCaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ManualCaptureModal: React.FC<ManualCaptureModalProps> = ({
  isOpen,
  onClose
}) => {
  if (!isOpen) return null;

  const downloadUrl = '/downloads/LushinDesktop-Setup.exe'; // Placeholder URL

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6 animate-fadeIn">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">🎤</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Manual Audio Capture</h2>
          <p className="text-gray-500 mt-2">
            Capture audio directly from your computer without using a meeting bot
          </p>
        </div>

        {/* Content */}
        <div className="space-y-4">
          {/* Requirements */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h3 className="font-semibold text-blue-800 mb-2">Requirements</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li className="flex items-center gap-2">
                <span className="text-blue-500">✓</span>
                Windows 10 or later
              </li>
              <li className="flex items-center gap-2">
                <span className="text-blue-500">✓</span>
                Microphone for your voice
              </li>
              <li className="flex items-center gap-2">
                <span className="text-blue-500">✓</span>
                System audio for customer voice
              </li>
            </ul>
          </div>

          {/* How it works */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-800 mb-2">How it works</h3>
            <ol className="text-sm text-gray-600 space-y-2">
              <li className="flex items-start gap-2">
                <span className="bg-gray-200 rounded-full w-5 h-5 flex items-center justify-center text-xs font-medium flex-shrink-0">1</span>
                <span>Download and install the Lushin desktop app</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-gray-200 rounded-full w-5 h-5 flex items-center justify-center text-xs font-medium flex-shrink-0">2</span>
                <span>Sign in with your Google account</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-gray-200 rounded-full w-5 h-5 flex items-center justify-center text-xs font-medium flex-shrink-0">3</span>
                <span>Click "Start Session" to begin capturing audio</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-gray-200 rounded-full w-5 h-5 flex items-center justify-center text-xs font-medium flex-shrink-0">4</span>
                <span>View real-time analysis here on the dashboard</span>
              </li>
            </ol>
          </div>

          {/* Note about macOS */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> Currently Windows only. macOS support coming soon.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-6 space-y-3">
          <a
            href={downloadUrl}
            className="block w-full bg-blue-500 hover:bg-blue-600 text-white text-center py-3 px-4 rounded-lg font-medium transition-colors"
            download
          >
            Download Desktop App
          </a>
          <button
            onClick={onClose}
            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 px-4 rounded-lg font-medium transition-colors"
          >
            Cancel
          </button>
        </div>

        {/* Footer */}
        <p className="text-xs text-center text-gray-400 mt-4">
          Already have the app? Just open it and sign in to start capturing.
        </p>
      </div>
    </div>
  );
};

export default ManualCaptureModal;
